import React, {useState, useEffect, useCallback} from 'react';
import {
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    TextField,
    Autocomplete,
    Box,
    Typography,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import {useAuth} from '../providers/AuthProvider';
import {useStatus} from '../providers/MsgStatusProvider';
import MastodonLogo from "./MastodonLogo";

const MastodonConnectButton = () => {
    const [open, setOpen] = useState(false);
    const [instanceUrl, setInstanceUrl] = useState('');
    const [instanceOptions, setInstanceOptions] = useState([]);
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [instanceInfo, setInstanceInfo] = useState(null);
    const {token} = useAuth();
    const {setStatusType, setStatusMessage, setIsAlertOpen} = useStatus();

    // Popular Mastodon instances
    const POPULAR_INSTANCES = [
        { url: 'https://mastodon.social', name: 'Mastodon Social', users: '900K+', description: 'The flagship instance' },
        { url: 'https://mas.to', name: 'Mas.to', users: '50K+', description: 'General purpose instance' },
        { url: 'https://fosstodon.org', name: 'Fosstodon', users: '40K+', description: 'FOSS enthusiasts' },
        { url: 'https://mastodon.world', name: 'Mastodon World', users: '100K+', description: 'Global community' },
        { url: 'https://mstdn.social', name: 'Mstdn Social', users: '80K+', description: 'Social instance' },
        { url: 'https://hachyderm.io', name: 'Hachyderm', users: '25K+', description: 'Tech professionals' },
        { url: 'https://pixelfed.social', name: 'Pixelfed Social', users: '15K+', description: 'Photo sharing' },
        { url: 'https://masto.ai', name: 'Masto AI', users: '10K+', description: 'AI and tech focus' }
    ];

    // Load saved instances and combine with popular ones
    useEffect(() => {
        const loadInstanceOptions = async () => {
            try {
                // Fetch user's previously connected instances
                const savedInstancesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}connect/mastodon/instances/`, {
                    headers: {
                        'Authorization': `Token ${token}`
                    }
                });
                
                let savedInstances = [];
                if (savedInstancesResponse.ok) {
                    const data = await savedInstancesResponse.json();
                    savedInstances = data.instances || [];
                }

                // Combine popular instances with saved ones
                const allInstances = [
                    ...savedInstances.map(instance => ({
                        ...instance,
                        isSaved: true
                    })),
                    ...POPULAR_INSTANCES.filter(
                        popular => !savedInstances.find(saved => saved.url === popular.url)
                    )
                ];

                setInstanceOptions(allInstances);
            } catch (error) {
                console.warn('Failed to load saved instances:', error);
                setInstanceOptions(POPULAR_INSTANCES);
            }
        };

        if (open) {
            loadInstanceOptions();
        }
    }, [open, token]);

    // Validate instance URL
    const validateInstance = useCallback(async (url) => {
        if (!url || url.length < 8) return;
        
        // Don't validate if it's a saved instance
        const savedInstance = instanceOptions.find(option => option.url === url && option.isSaved);
        if (savedInstance) {
            setInstanceInfo(savedInstance);
            setValidationError('');
            return;
        }

        setIsValidating(true);
        setValidationError('');
        setInstanceInfo(null);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}connect/mastodon/validate-instance/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                },
                body: JSON.stringify({ instance_url: url })
            });

            const data = await response.json();

            if (data.valid) {
                setInstanceInfo(data.instance_info);
                setValidationError('');
            } else {
                setValidationError(data.error || 'Invalid Mastodon instance');
                setInstanceInfo(null);
            }
        } catch (error) {
            setValidationError('Failed to validate instance. Please check the URL.');
            setInstanceInfo(null);
        } finally {
            setIsValidating(false);
        }
    }, [instanceOptions, token]);

    // Debounced validation
    useEffect(() => {
        if (!instanceUrl) {
            setValidationError('');
            setInstanceInfo(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            validateInstance(instanceUrl);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [instanceUrl, validateInstance]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setInstanceUrl('');
        setValidationError('');
        setInstanceInfo(null);
    };

    const handleConnect = async () => {
        if (!instanceUrl) {
            setValidationError('Please select or enter a Mastodon instance URL');
            return;
        }

        if (validationError) {
            setStatusMessage('Please fix the instance URL error before connecting');
            setIsAlertOpen(true);
            setStatusType('error');
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}connect/mastodon/auth/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                },
                body: JSON.stringify({ instance_url: instanceUrl })
            });

            const data = await response.json();

            if (data.redirect_url) {
                window.location.href = data.redirect_url;
            } else {
                setStatusMessage(data.error || 'Failed to connect to Mastodon');
                setIsAlertOpen(true);
                setStatusType('error');
            }
        } catch (error) {
            setStatusMessage('Error connecting to Mastodon');
            setIsAlertOpen(true);
            setStatusType('error');
        }
        handleClose();
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<MastodonLogo />}
                onClick={handleClickOpen}
            >
                Connect to Mastodon
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <MastodonLogo />
                        <Typography variant="h6">Connect Mastodon Account</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Choose your Mastodon instance to connect your account
                    </Typography>
                </DialogTitle>
                
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Autocomplete
                            freeSolo
                            options={instanceOptions}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.url || option.name || '';
                            }}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="subtitle2">
                                                {option.name || option.url}
                                            </Typography>
                                            {option.isSaved && (
                                                <Chip label="Previously used" size="small" color="primary" />
                                            )}
                                            {option.users && (
                                                <Chip label={option.users} size="small" variant="outlined" />
                                            )}
                                        </Box>
                                        {option.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {option.description}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {option.url}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            inputValue={instanceUrl}
                            onInputChange={(event, newInputValue) => {
                                setInstanceUrl(newInputValue);
                            }}
                            onChange={(event, newValue) => {
                                if (typeof newValue === 'string') {
                                    setInstanceUrl(newValue);
                                } else if (newValue && newValue.url) {
                                    setInstanceUrl(newValue.url);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Mastodon Instance URL"
                                    placeholder="https://mastodon.social or search..."
                                    variant="outlined"
                                    fullWidth
                                    error={!!validationError}
                                    helperText={validationError || "Enter a Mastodon instance URL or select from the list"}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isValidating && <CircularProgress size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        {instanceInfo && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                                <Typography variant="subtitle2" color="success.dark">
                                    ✓ Valid Mastodon Instance
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>{instanceInfo.title || 'Instance'}</strong>
                                    {instanceInfo.description && (
                                        <span> - {instanceInfo.description}</span>
                                    )}
                                </Typography>
                                {instanceInfo.max_toot_chars && (
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                        Character limit: {instanceInfo.max_toot_chars}
                                    </Typography>
                                )}
                                {instanceInfo.version && (
                                    <Typography variant="caption" display="block">
                                        Version: {instanceInfo.version}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {validationError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {validationError}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConnect}
                        variant="contained"
                        color="primary"
                        disabled={!instanceUrl || !!validationError || isValidating}
                        startIcon={isValidating ? <CircularProgress size={16} /> : null}
                    >
                        {isValidating ? 'Validating...' : 'Connect'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MastodonConnectButton; 